import {
  MatchScoreStatus,
  TournamentGameStatus,
} from "../../../../../../gql/graphql";

/* ============================================
   Shared interfaces for bracket views
   ============================================ */

export interface TeamScore {
  teamId: string;
  status: MatchScoreStatus;
  score: number;
  matchId: string;
}

export interface Team {
  id: string;
  name: string;
}

export interface Match {
  id: string;
  order: number;
  status: MatchScoreStatus;
}

export interface Game {
  id: string;
  order: number;
  status: TournamentGameStatus;
  losingGameId: string;
  winningGameId: string;
  configuration: {
    wonMatchCountToWinGame?: number;
  };
  matches: Match[];
}

export interface Round {
  id: string;
  name: string;
  order: number;
  games: Game[];
}

export interface BracketGroup {
  id: string;
  name: string;
  rounds: Round[];
}

/* ============================================
   Shared utilities
   ============================================ */

export interface GameTeamData {
  team: Team | null;
  teamScores: TeamScore[];
  aggregateStatus: MatchScoreStatus;
  totalScore: number;
}

/**
 * Determine the aggregate game-level status for a team based on
 * their per-match scores and the game's win configuration.
 */
export const getTeamGameStatus = (
  teamScores: TeamScore[],
  game: Game,
): MatchScoreStatus => {
  let winCount = 0;
  const winsNeeded = game.configuration?.wonMatchCountToWinGame ?? 1;

  for (const score of teamScores) {
    if (score.status === MatchScoreStatus.Forfeit) {
      return MatchScoreStatus.Forfeit;
    }
    if (score.status === MatchScoreStatus.Winner) {
      winCount++;
      if (winCount >= winsNeeded) {
        return MatchScoreStatus.Winner;
      }
    }
  }

  const hasLoss = teamScores.some((s) => s.status === MatchScoreStatus.Loser);
  if (hasLoss && teamScores.length >= game.matches.length) {
    return MatchScoreStatus.Loser;
  }

  return MatchScoreStatus.Waiting;
};

/**
 * Build a team lookup map from a teams array.
 */
export const buildTeamMap = (teams: Team[]): Map<string, Team> => {
  const map = new Map<string, Team>();
  teams.forEach((team) => map.set(team.id, team));
  return map;
};

/**
 * Extract the teams participating in a game from the scores array,
 * computing aggregate status and total score for each.
 */
export const getGameTeams = (
  game: Game,
  scores: TeamScore[],
  teamMap: Map<string, Team>,
): GameTeamData[] => {
  const gameMatchIds = new Set(game.matches.map((m) => m.id));
  const teamIds = new Set<string>();
  scores.forEach((s) => {
    if (gameMatchIds.has(s.matchId)) {
      teamIds.add(s.teamId);
    }
  });

  return Array.from(teamIds).map((teamId) => {
    const team = teamMap.get(teamId) ?? null;
    const teamScores = scores.filter(
      (s) => s.teamId === teamId && gameMatchIds.has(s.matchId),
    );
    const aggregateStatus = getTeamGameStatus(teamScores, game);
    const totalScore = teamScores.reduce((sum, s) => sum + s.score, 0);
    return { team, teamScores, aggregateStatus, totalScore };
  });
};

/**
 * Convert a GameTeamData entry into the shape expected by MatchScoreCard.
 */
export const toCardTeam = (
  data: GameTeamData | null,
): {
  teamId: string;
  teamName: string;
  status: MatchScoreStatus;
  score: number;
  matchDetails: { matchId: string; score: number; status: MatchScoreStatus }[];
} | null => {
  if (!data?.team) return null;
  return {
    teamId: data.team.id,
    teamName: data.team.name,
    status: data.aggregateStatus,
    score: data.totalScore,
    matchDetails: data.teamScores.map((s) => ({
      matchId: s.matchId,
      score: s.score,
      status: s.status,
    })),
  };
};
